import classNames from 'classnames';
import { Link } from 'core/i18n/navigation';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';

const SHOPPING_KEYS = ['item1', 'item2', 'item3', 'item4', 'item5'];

// Memory-keeping use case — "Memos" showcase with floating memo cards.
export const Section29 = () => {
  const t = useTranslations();
  const m = (key: string) => t(`pages.useCases.memoryKeeping.memos.${key}`);
  return (
    <section id="how" className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.stage}>
          <div className={styles.backdrop} aria-hidden="true" />

          {/* Shopping list memo */}
          <div
            className={`${styles.memo} left-0 top-0 z-[3] w-[280px] motion-safe:animate-floaty`}
          >
            <div className={styles.memoTitle}>{m('shopping.title')}</div>
            <div className="flex flex-col gap-2">
              {SHOPPING_KEYS.map((key) => (
                <div key={key} className={styles.checkRow}>
                  <span className={styles.checkbox} />
                  {m(`shopping.${key}`)}
                </div>
              ))}
            </div>
            <div className={styles.memoFoot}>
              <span>{m('shopping.box')}</span>
              <span>{m('shopping.time')}</span>
            </div>
          </div>

          {/* Quote memo */}
          <div
            className={`${styles.quote} right-0 top-[150px] z-[4] w-[280px] motion-safe:animate-floaty-slow`}
            style={{ rotate: '2deg' }}
          >
            <div className={styles.quoteMark}>&ldquo;</div>
            <blockquote className={styles.quoteText}>
              {m('quote.text')}
            </blockquote>
            <div className={styles.quoteFoot}>
              <span>{m('quote.box')}</span>
              <span>{m('quote.time')}</span>
            </div>
          </div>

          {/* Recipe memo */}
          <div
            className={`${styles.memo} bottom-0 left-[70px] z-[2] w-[270px] motion-safe:animate-floaty-delayed`}
            style={{ rotate: '-1.5deg' }}
          >
            <div className={styles.memoTitle}>{m('recipe.title')}</div>
            <p className={styles.recipe}>{m('recipe.body')}</p>
            <div className={styles.memoFoot}>
              <span>{m('recipe.box')}</span>
              <span>{m('recipe.time')}</span>
            </div>
          </div>
        </div>

        <div className={styles.copy}>
          <div className={styles.eyebrow}>{m('eyebrow')}</div>
          <h2 className={styles.title}>{m('title')}</h2>
          <p className={styles.lead}>{m('lead')}</p>
          <Link href={appRoutes.LOGIN} className={styles.link}>
            {m('link')}
            <span className={styles.linkIcon}>
              <svg
                viewBox="0 0 24 24"
                className={styles.arrow}
                aria-hidden="true"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:px-10',
  stage: 'relative mx-auto h-[560px] w-[360px]',
  backdrop:
    'absolute -left-6 right-6 top-10 bottom-10 rounded-[24px] bg-[#EBF0F7] marketing-dark:bg-white/[0.03]',
  memo: classNames(
    'absolute rounded-[18px] border p-[18px]',
    'border-[#F0F0F0] bg-white shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f]',
    'marketing-dark:shadow-[0_20px_50px_rgba(2,8,30,0.5)]',
  ),
  memoTitle:
    'mb-3 text-sm font-bold text-mf-off-black-600 marketing-dark:text-white',
  checkRow:
    'flex items-center gap-2 text-sm text-mf-gray-600 marketing-dark:text-white/75',
  checkbox:
    'h-4 w-4 flex-shrink-0 rounded border border-mf-gray-300 marketing-dark:border-white/25',
  memoFoot: classNames(
    'mt-3 flex items-center justify-between border-t pt-2 text-xs',
    'border-[#F0F0F0] text-mf-gray-500',
    'marketing-dark:border-white/10 marketing-dark:text-white/45',
  ),
  // The blue quote card keeps its brand look in both themes; only the shadow
  // softens on the light page.
  quote: classNames(
    'absolute rounded-[18px] border border-white/10 bg-mf-blue/90 p-[22px]',
    'shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:shadow-[0_20px_50px_rgba(2,8,30,0.5)]',
  ),
  quoteMark: 'font-serif text-[44px] leading-[0.6] text-white/50',
  quoteText: 'mt-1 text-[15px] font-medium leading-[1.55] text-white',
  quoteFoot:
    'mt-3 flex items-center justify-between border-t border-white/15 pt-3 text-xs text-white/60',
  recipe: 'text-xs leading-[1.6] text-mf-gray-600 marketing-dark:text-white/70',
  copy: 'text-center lg:text-left',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  lead: 'mx-auto mt-6 max-w-[480px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-xl',
  link: classNames(
    'mt-8 inline-flex items-center gap-3 text-base font-semibold transition-colors',
    'text-mf-blue hover:text-mf-off-black-600',
    'marketing-dark:text-mf-coral marketing-dark:hover:text-white',
  ),
  linkIcon:
    'flex h-11 w-11 items-center justify-center rounded-full bg-mf-blue text-white',
  arrow: 'h-[18px] w-[18px] fill-none stroke-current stroke-2',
};
